import React, { useState, useCallback, useMemo } from 'react';
import { DollarSign, Users, Receipt, Percent } from 'lucide-react';

const TIP_PRESETS = [10, 15, 18, 20, 25, 30];

const TipCalculator: React.FC = () => {
  const [billAmount, setBillAmount] = useState('');
  const [tipPercent, setTipPercent] = useState(18);
  const [customTip, setCustomTip] = useState('');
  const [splitCount, setSplitCount] = useState(1);
  const [isCustomTip, setIsCustomTip] = useState(false);

  const activeTip = isCustomTip ? (parseFloat(customTip) || 0) : tipPercent;
  const bill = parseFloat(billAmount) || 0;

  const result = useMemo(() => {
    const tipAmount = bill * (activeTip / 100);
    const total = bill + tipAmount;
    const perPerson = splitCount > 0 ? total / splitCount : total;
    const tipPerPerson = splitCount > 0 ? tipAmount / splitCount : tipAmount;
    return { tipAmount, total, perPerson, tipPerPerson };
  }, [bill, activeTip, splitCount]);

  const handlePreset = useCallback((pct: number) => {
    setTipPercent(pct);
    setIsCustomTip(false);
    setCustomTip('');
  }, []);

  const handleCustom = useCallback((val: string) => {
    setCustomTip(val);
    setIsCustomTip(true);
  }, []);

  const roundUp = useCallback(() => {
    if (result.total > 0) {
      const rounded = Math.ceil(result.total);
      const newTip = rounded - bill;
      const newPct = bill > 0 ? (newTip / bill) * 100 : 0;
      setCustomTip(newPct.toFixed(1));
      setIsCustomTip(true);
    }
  }, [result.total, bill]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-green-100 rounded-lg"><Receipt className="w-6 h-6 text-green-600" /></div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Tip Calculator</h2>
          <p className="text-sm text-gray-500">Calculate tips and split bills easily</p>
        </div>
      </div>

      {/* Bill amount */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-1">Bill Amount</label>
        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="number" min="0" step="0.01" value={billAmount}
            onChange={(e) => setBillAmount(e.target.value)}
            placeholder="0.00"
            className="w-full pl-10 pr-4 py-3 text-xl border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-900"
          />
        </div>
      </div>

      {/* Tip presets */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-2">Tip Percentage</label>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
          {TIP_PRESETS.map((pct) => (
            <button
              key={pct}
              onClick={() => handlePreset(pct)}
              className={`py-2.5 rounded-lg text-sm font-semibold transition ${
                !isCustomTip && tipPercent === pct
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {pct}%
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Custom:</span>
          <div className="relative flex-1">
            <input
              type="number" min="0" max="100" step="0.5" value={customTip}
              onChange={(e) => handleCustom(e.target.value)}
              placeholder="Custom %"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 text-gray-900 ${
                isCustomTip ? 'border-green-500 bg-green-50' : 'border-gray-300'
              }`}
            />
            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Split */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Split Between</label>
        <div className="flex items-center gap-3">
          <button onClick={() => setSplitCount(Math.max(1, splitCount - 1))}
            className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-lg font-bold transition">-</button>
          <div className="flex items-center gap-2 flex-1 justify-center">
            <Users className="w-5 h-5 text-gray-400" />
            <span className="text-2xl font-bold text-gray-900">{splitCount}</span>
            <span className="text-sm text-gray-500">{splitCount === 1 ? 'person' : 'people'}</span>
          </div>
          <button onClick={() => setSplitCount(splitCount + 1)}
            className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-lg font-bold transition">+</button>
        </div>
      </div>

      {/* Results */}
      {bill > 0 && (
        <div className="space-y-3">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-100">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Tip Amount</p>
                <p className="text-2xl font-bold text-green-600">${result.tipAmount.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Total</p>
                <p className="text-2xl font-bold text-gray-900">${result.total.toFixed(2)}</p>
              </div>
            </div>

            {splitCount > 1 && (
              <div className="pt-3 border-t border-green-200 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Tip per person</p>
                  <p className="text-lg font-bold text-green-600">${result.tipPerPerson.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Total per person</p>
                  <p className="text-lg font-bold text-gray-900">${result.perPerson.toFixed(2)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Round up */}
          <button onClick={roundUp}
            className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition">
            Round total up to ${Math.ceil(result.total).toFixed(2)}
          </button>

          {/* Tip guide */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Quick Guide</h4>
            <div className="grid grid-cols-3 gap-2 text-xs text-center">
              <div className="p-2 bg-white rounded border"><p className="font-bold text-gray-900">10-15%</p><p className="text-gray-500">Average</p></div>
              <div className="p-2 bg-white rounded border"><p className="font-bold text-gray-900">18-20%</p><p className="text-gray-500">Good</p></div>
              <div className="p-2 bg-white rounded border"><p className="font-bold text-gray-900">25%+</p><p className="text-gray-500">Excellent</p></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TipCalculator;
